// rivalPersona.ts — BYT3's face and voice. The rival AI is deliberately fallible (belief = truth
// XOR noise), but until now that personality only existed as movement timing. This module gives it
// a visible, audible persona WITHOUT touching Core: an emote avatar on the rival's HUD readout
// (expression portraits swapped by game events) plus reactive VO barks (audio.playBark — a second
// voice lane that always yields to the announcer). Pure event-log consumer, renderer-only.
//
// Fallback contract: if the emote portrait textures haven't been generated/registered (hasTex
// false), buildFace() declines and GameScene keeps its geometric triangle — persona then still
// barks but never shows a face. Nothing here is load-bearing for gameplay.
import Phaser from 'phaser';
import { audio, type BarkKey, type OptionalBarkKey } from './audio';
import { tex, hasTex, KEYS, FONT, type LogicalKey } from './keys';
import { motionAllowed } from './settings';
import { requestCaption, CAPTION_PRIORITY } from './captionSlot';
import { grudgeBoardStartLine, grudgeLineClipKey } from './rivalMemory';

export type RivalEmote = 'neutral' | 'thinking' | 'gloat' | 'panic' | 'embarrassed';

/** The rival's display name — original IP, theme-noun-free (style-bible: arcade robot). */
export const RIVAL_NAME = 'BYT3';

/**
 * Per-MATCH once-guard for BYT3's grudge cold-open, kept on the Phaser registry — NOT on this per-board
 * persona instance (RivalPersona is reconstructed every board, so an instance flag could never span the
 * warmup→join boundary). GameScene.init resets it at match start (board 0); reactBoardStart sets it when
 * the opener fires. Net: the relationship-aware cold-open plays EXACTLY ONCE per match, on BYT3's JOIN
 * board — the first board where the persona is enabled (organic flow: board 1, after the persona-disabled
 * WARMUP board 0; direct ?ai= / NEW MATCH / ladder: board 0) — and never re-fires on boards 2-4.
 */
const GRUDGE_COLD_OPEN_KEY = 'rival.grudgeColdOpenShown';

/** Re-arm BYT3's grudge cold-open for a new match. Call once at match start (GameScene.init, board 0). */
export function resetGrudgeColdOpen(scene: Phaser.Scene): void {
  scene.registry.set(GRUDGE_COLD_OPEN_KEY, false);
}
function grudgeColdOpenShown(scene: Phaser.Scene): boolean {
  return scene.registry.get(GRUDGE_COLD_OPEN_KEY) === true;
}
function markGrudgeColdOpenShown(scene: Phaser.Scene): void {
  scene.registry.set(GRUDGE_COLD_OPEN_KEY, true);
}

const EMOTE_TEX: Record<Exclude<RivalEmote, 'neutral'>, LogicalKey> = {
  thinking: KEYS.rivalEmoteThinking,
  gloat: KEYS.rivalEmoteGloat,
  panic: KEYS.rivalEmotePanic,
  embarrassed: KEYS.rivalEmoteEmbarrassed,
};

/**
 * Caption text per bark, PER TAKE — the accessibility layer (shows even under mute) + BYT3's name
 * reveal. Index [0]/[1] MUST match the spoken take the audio bus returns from playBark (take 0 =
 * bark-*.mp3, take 1 = bark-*-2.mp3), or the on-screen line drifts from the voice.
 */
const BARK_LINE: Record<BarkKey, readonly [string, string]> = {
  barkStart: ['Ready to lose?', 'My board now!'],
  barkCorrect: ['Mine!', 'Yoink!'],
  barkDecoy: ['Bleh! Decoy!', 'Who put THAT there?!'],
  barkLead: ['Too slow!', 'Eat my dust!'],
  barkBehind: ['Wait, wait, wait!', 'Recalculating!'],
  barkThreaded: ['Threaded?! Show-off.', 'How did you DO that?!'],
  barkBoardWin: ['Gotcha!', 'Too easy!'],
  barkBoardLoss: ['No, no, no, no!', 'This does not compute!'],
  barkMatchWin: ['I am unbeatable!', 'Victory logged!'],
  barkMatchLoss: ['Rematch. Now.', 'Error. Error. ERROR!'],
  // GHOST DUELS Slice D — gatekeeper ladder lines. SINGLE-take (both entries identical): playBark never
  // registers a take-2 for these, so it always returns take 0 and captions index [0].
  barkLadderIntro: ['You beat me. Now beat THEM.', 'You beat me. Now beat THEM.'],
  barkRungWin: ["Ghost down! Who's next?", "Ghost down! Who's next?"],
  barkRungLoss: ['Even the GHOSTS are beating you!', 'Even the GHOSTS are beating you!'],
  barkLadderClear: ['You... beat them ALL?!', 'You... beat them ALL?!'],
};

/** The logical texture for an emote portrait (neutral reuses the accepted aiIdle character). */
function emoteTexKey(emote: RivalEmote): LogicalKey {
  return emote === 'neutral' ? KEYS.aiIdle : EMOTE_TEX[emote];
}

/**
 * Is the FULL emote portrait set registered? All-or-nothing (like buildFace): a mixed set (some real,
 * some missing) would pop between styles. Gates both the readout avatar and the caption-plate portrait.
 */
function emotePortraitsReady(): boolean {
  if (!hasTex(KEYS.aiIdle)) return false;
  for (const k of Object.values(EMOTE_TEX)) if (!hasTex(k)) return false;
  return true;
}

/** Result-surface bark mood → portrait (wins gloat; match loss panics; board loss embarrassed). */
function resultEmote(key: BarkKey): RivalEmote {
  // Gatekeeper gloats over a fallen ghost (rungWin) and while mocking a rung loss; stunned when the
  // whole ladder falls; embarrassed as he hands you off after losing the daily (ladderIntro).
  if (key === 'barkBoardWin' || key === 'barkMatchWin' || key === 'barkRungWin' || key === 'barkRungLoss') return 'gloat';
  if (key === 'barkMatchLoss' || key === 'barkLadderClear') return 'panic';
  return 'embarrassed';
}

export class RivalPersona {
  private scene: Phaser.Scene;
  private face?: Phaser.GameObjects.Image;
  private faceSize = 30;
  private emote: RivalEmote = 'neutral';
  private holdUntilMs = 0; // event emotes hold briefly, then fall back to thinking/neutral
  private thinking = false;
  private enabled: boolean;

  constructor(scene: Phaser.Scene, enabled: boolean) {
    this.scene = scene;
    this.enabled = enabled;
  }

  /**
   * Add the emote avatar to the rival readout container at (x,y). Returns false (and adds nothing)
   * when the portrait set isn't registered — the caller keeps its geometric triangle.
   */
  buildFace(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    size: number,
    opts: { visible?: boolean } = {},
  ): boolean {
    if (!this.enabled) return false;
    // Neutral face is the accepted aiIdle character; the four expressions must ALL be real art —
    // a mixed set (some real, some missing) would pop between styles, worse than no face.
    if (!emotePortraitsReady()) return false;
    this.faceSize = size;
    this.face = this.scene.add.image(x, y, tex(KEYS.aiIdle)).setOrigin(0.5);
    this.sizeFace();
    // S6 (Decision 4 — ONE identity): when the readout's RESTING avatar is BYT3's pose CREATURE (the
    // chip pose path), the persona face rides HIDDEN — it stays the caption anchor + gloat/panic
    // expression surface (getBounds stays valid while invisible, so barks/captions still fire), but the
    // visible chip identity is the pose creature. Default (visible) = the emote-portrait chip fallback.
    if (opts.visible === false) this.face.setVisible(false);
    container.add(this.face);
    return true;
  }

  /** Re-anchor the face during readout relayout (score width changes shift the row). */
  layout(x: number, y: number): void {
    this.face?.setPosition(x, y);
  }

  hasFace(): boolean {
    return this.face !== undefined;
  }

  /** Grace-window "pondering" state — driven per-frame by the readout (never overrides event holds). */
  setThinking(on: boolean): void {
    if (this.thinking === on) return;
    this.thinking = on;
    this.refresh();
  }

  // --- semantic reactions (GameScene event branches call these) -------------------------------

  /** Board start: BYT3 talks a little trash. On his JOIN board — the first board of the match where the
   *  persona is enabled (organic flow: board 1, after the persona-disabled WARMUP board 0; direct ?ai= /
   *  NEW MATCH / ladder: board 0) — he ALWAYS barks an opener, and when a cross-session grudge exists the
   *  caption becomes a relationship-aware line (H2H record, a revenge/fluke callback, a thread-fear
   *  warning, or a returning-after-lapse jab) — VOICE only; the spoken take stays the generic barkStart
   *  clip (grudge VO waits for S7). On later boards he trash-talks ~30% of the time. Empty memory → the
   *  stateless opener.
   *
   *  Ladder duels are EXCLUDED from the grudge READ: the opponent there is a stored GHOST, not
   *  BYT3-the-match-rival, so a head-to-head "You are 3 and 11 against me" line would be incoherent — a
   *  ladder join still gets the guaranteed opener, just the stateless one. The once-per-match JOIN
   *  detection lives on the registry (grudgeColdOpen*, reset at match start), so the FIRST enabled call of
   *  the match is the join board and boards 2-4 never re-fire the cold-open. */
  reactBoardStart(isLadderDuel: boolean): void {
    if (!this.enabled) return;
    if (!grudgeColdOpenShown(this.scene)) {
      // First enabled board of the match = BYT3's join board: always open, and ride the grudge caption
      // when he has real match history to reference (never on a ghost-ladder duel).
      markGrudgeColdOpenShown(this.scene);
      const grudge = isLadderDuel ? undefined : grudgeBoardStartLine() ?? undefined;
      // S7b: when the picked grudge line has a recorded VO clip, BYT3 SPEAKS it — the grudge clip REPLACES
      // the generic barkStart take (both never play). A dynamic {placeholder} line or empty memory keeps
      // the generic opener + (when present) the caption-only grudge text — the prior behavior.
      const clip = grudge ? grudgeLineClipKey(grudge) : null;
      if (clip && audio().hasOptionalBark(clip)) this.grudgeColdOpen(clip, grudge!);
      else this.genericColdOpen(grudge);
      return;
    }
    // Later-board opener (boards 2-4): a board-start persona line, so its CAPTION always shows even if a
    // topic-intro VO holds the announcer-yield window — the voice stays best-effort. No cold-open retry
    // (this is ambient trash-talk, not the once-per-match guaranteed beat).
    if (Math.random() < 0.3) this.barkAlwaysCaption('barkStart');
  }

  /**
   * BYT3's once-per-match grudge COLD-OPEN with a recorded VO clip (it REPLACES the generic opener take).
   * The CAPTION is the accessibility layer, so it ALWAYS shows now — even when a topic-intro VO holds the
   * 1600ms announcer-yield window and the voice can't fire (the prior path dropped BOTH voice AND caption,
   * stranding a muted/deaf player with nothing). The VOICE is best-effort: play the clip once; if it
   * yielded to the announcer, retry ONCE ~1.8s later. Single-voice discipline is preserved — the retry
   * still respects the yield/self-throttle inside playOptionalBark, so it never talks over the topic-intro
   * VO; if it yields again nothing more fires and the caption already showed. No double-caption, no double-voice.
   */
  private grudgeColdOpen(clip: OptionalBarkKey, line: string): void {
    this.caption(line); // a11y: unconditional
    if (!audio().playOptionalBark(clip)) this.retryColdOpenVoice(() => void audio().playOptionalBark(clip));
  }

  /**
   * The generic (or caption-only-grudge) once-per-match cold-open. Same contract as grudgeColdOpen: the
   * CAPTION always shows; the VOICE (the generic barkStart take) is best-effort with ONE ~1.8s retry.
   * captionOverride carries a caption-only grudge line (a dynamic {w}{l} line, or one with no recorded
   * clip); empty memory → the generic opener's take-matched line (take 0 when the voice is suppressed at
   * caption time, so the a11y line still reads — a rare first-match-only take/caption drift is acceptable).
   */
  private genericColdOpen(captionOverride?: string): void {
    const take = audio().playBark('barkStart');
    this.caption(captionOverride ?? BARK_LINE.barkStart[take >= 0 ? take : 0] ?? BARK_LINE.barkStart[0]);
    if (take < 0) this.retryColdOpenVoice(() => void audio().playBark('barkStart'));
  }

  /**
   * ONE deferred voice-only retry for the cold-open (~1.8s later). The caption already showed, so this
   * NEVER spawns another. `attempt` fires the voice through the same audio-bus path (playBark /
   * playOptionalBark), which re-checks the announcer yield + self-throttle internally — a still-busy
   * announcer simply drops it (no double-voice, no talk-over the topic-intro VO).
   */
  private retryColdOpenVoice(attempt: () => void): void {
    this.scene.time.delayedCall(1800, attempt);
  }

  /** The rival ate a correct answer. Quick gloat; occasional "Mine!". */
  reactRivalCorrect(): void {
    if (!this.enabled) return;
    this.show('gloat', 1200);
    if (Math.random() < 0.35) this.bark('barkCorrect');
  }

  /** The rival bit a decoy — its signature beatable-fallibility moment. Always voiced. */
  reactRivalDecoy(): void {
    if (!this.enabled) return;
    this.show('embarrassed', 2000);
    this.bark('barkDecoy', 2500);
  }

  /** The HUMAN threaded the Gauntlet — BYT3 gets rattled, snarks AFTER the announcer's line. */
  reactPlayerThreaded(): void {
    if (!this.enabled) return;
    this.show('panic', 1800);
    this.scheduleThreadedBark(0);
  }

  /**
   * Defer BYT3's snark until the announcer's THREADED IT / GAUNTLET CHAIN callout has actually
   * FINISHED, then bark. A single fixed delay can't do this: the chain crescendo runs a slow-mo that
   * stretches scene-time, so the old 950ms delayedCall could land the snark past the fixed yield
   * window while a long chain line was still playing (audio review: overlap @00:15 / @00:39). We
   * poll the real announcer end (announcerBusy) and reschedule once more if it's still speaking,
   * capped so the snark never drops entirely — a slightly late reaction beats one talking over the call.
   */
  private scheduleThreadedBark(attempt: number): void {
    this.scene.time.delayedCall(attempt === 0 ? 950 : 900, () => {
      if (attempt < 3 && audio().announcerBusy()) {
        this.scheduleThreadedBark(attempt + 1);
        return;
      }
      this.bark('barkThreaded');
    });
  }

  /** Lead flipped (edge-triggered by the readout's dirty-check). sign: +1 human leads. */
  reactLeadChange(sign: number, gap: number): void {
    // 60 = one decoy penalty, the smallest real score move: a first lead flip is typically ±100
    // (one correct), so any higher threshold silences the most common flip of the match.
    if (!this.enabled || Math.abs(gap) < 60) return; // ignore zero-churn ties only
    if (sign < 0) {
      this.show('gloat', 1600);
      this.bark('barkLead');
    } else {
      this.show('panic', 1600);
      this.bark('barkBehind');
    }
  }

  /** Board settled — EMOTE only. The board-end VOICE fires from ResultScene (resultSceneBark):
   *  GameScene's clock dies at its 420ms Result handoff, so a delayed bark scheduled here could
   *  never play (independent review HIGH, 2026-07-02). */
  reactBoardEnd(humanWon: boolean): void {
    if (!this.enabled) return;
    this.show(humanWon ? 'embarrassed' : 'gloat', 2600);
  }

  /**
   * Voice + caption pair: fire the bark through the audio bus (it self-throttles and yields to the
   * announcer); when it actually fires, float the line as a small `BYT3: "…"` caption off the
   * readout. The caption is the accessibility layer — it shows even when the game is muted.
   */
  private bark(key: BarkKey, minGapMs?: number, captionOverride?: string): void {
    const take = audio().playBark(key, { minGapMs });
    if (take < 0) return; // suppressed (announcer owns the moment / self-throttle)
    // captionOverride carries a relationship-aware grudge line (VOICE only); the spoken take is unchanged.
    this.caption(captionOverride ?? BARK_LINE[key][take] ?? BARK_LINE[key][0]);
  }

  /**
   * Voice + GUARANTEED caption. Fire the bark, but float the line as a caption WHETHER OR NOT it actually
   * voiced (announcer-yield / self-throttle can suppress it). Used for the board-start opener persona
   * lines, where a dropped caption would silently strand a muted/deaf player. When the voice is suppressed
   * there is no spoken take, so the caption falls back to the override (grudge text) or take 0's line.
   */
  private barkAlwaysCaption(key: BarkKey, minGapMs?: number, captionOverride?: string): void {
    const take = audio().playBark(key, { minGapMs });
    this.caption(captionOverride ?? BARK_LINE[key][take >= 0 ? take : 0] ?? BARK_LINE[key][0]);
  }

  private caption(line: string): void {
    if (!this.face || !this.face.active) return;
    const b = this.face.getBounds(); // world-space (container transform applied)
    // Carry the CURRENT emote onto the caption plate: the 30px HUD face is illegible at phone size
    // (visual review: a blind viewer read the rival as text-only), so the ~46px plate portrait is
    // where the reaction actually reads as a character emoting.
    spawnCaption(this.scene, b.centerX, b.bottom + 6, line, this.emote);
  }

  // --- internals -------------------------------------------------------------------------------

  private show(emote: RivalEmote, holdMs: number): void {
    this.holdUntilMs = this.scene.time.now + holdMs;
    if (this.emote !== emote) {
      this.emote = emote;
      this.apply(emote, true);
    }
    // Expire the hold back to thinking/neutral (a hair late is fine; refresh re-resolves).
    this.scene.time.delayedCall(holdMs + 30, () => this.refresh());
  }

  /** Resolve what the face should show right now: live event hold > thinking > neutral. */
  private refresh(): void {
    if (this.scene.time.now < this.holdUntilMs) return; // an event emote still owns the face
    const next: RivalEmote = this.thinking ? 'thinking' : 'neutral';
    if (this.emote !== next) {
      this.emote = next;
      this.apply(next, false);
    }
  }

  private apply(emote: RivalEmote, pop: boolean): void {
    if (!this.face) return;
    const key = emote === 'neutral' ? KEYS.aiIdle : EMOTE_TEX[emote];
    this.face.setTexture(tex(key));
    this.sizeFace();
    if (pop && motionAllowed()) {
      this.scene.tweens.killTweensOf(this.face);
      this.face.setScale(this.face.scale * 1.35);
      this.scene.tweens.add({
        targets: this.face,
        scale: this.baseScale(),
        duration: 240,
        ease: 'Back.easeOut',
      });
    }
  }

  private baseScale(): number {
    if (!this.face) return 1;
    const src = Math.max(this.face.width, this.face.height, 1);
    return this.faceSize / src;
  }

  private sizeFace(): void {
    this.face?.setScale(this.baseScale());
  }
}

/** Lifetime of a persona caption card (fade-in + hold + fade-out) before it self-destroys. */
const CAPTION_LIFE_MS = 2100;

/**
 * The Y ceiling a floating caption card must stay ABOVE — the board's top edge. During a live board the
 * scene exposes `gridY`; a bigger emote portrait grows DOWN, so the card is lifted until its bottom
 * (portrait + float drift) clears the grid, closing the audit's "portrait parked on the Vancouver tile"
 * occlusion. On surfaces without a grid (Result) there is no ceiling — the full viewport is available.
 */
function gridCeiling(scene: Phaser.Scene): number {
  const gy = (scene as Partial<{ gridY: number }>).gridY;
  return typeof gy === 'number' && gy > 0 ? gy - 8 : scene.scale.height;
}

/**
 * Shared plated caption (Gemini persona-verify 2026-07-02: 16px bold-italic on a padded plate). When
 * an emote is supplied AND the portrait set is registered, a ~76px expression portrait rides to the
 * LEFT of the text so the reaction reads as a CHARACTER speaking, not a UI icon (fix-verify: the 46px
 * static portrait "read as an icon"). The portrait pops in (Back.easeOut) and bobs gently for the
 * caption's lifetime. Text-only when portraits aren't registered (hasTex contract). The plate +
 * portrait share a right-anchored container (depth 9000) so both hug the anchor edge and float
 * together; the container shows even under mute (accessibility) and auto-destroys.
 *
 * S3b gap 2: routed through the ONE shared caption slot (requestCaption) so BYT3 and a raced ghost can
 * never stack two bubbles — only one speech card is on screen at a time. The card's top-left corner is
 * clamped so the portrait can never dip onto a live board tile (gridCeiling), so the emote always
 * floats ABOVE the grid.
 *
 * Collision: the portrait is TOP-ANCHORED at the container origin (never extends above y=0), so a
 * bigger portrait grows DOWN — it can't climb into the rival readout pill above the readout caption.
 */
function spawnCaption(
  scene: Phaser.Scene,
  x: number,
  y: number,
  line: string,
  emote?: RivalEmote,
): void {
  requestCaption(
    scene,
    (sc) => {
      // Item 5 (S5) — RAIL-MIRROR the plate the same way S4 fixed ghost captions: on the LEFT rail (the
      // landscape readout sits left) grow the card RIGHT so a long line can't spill off the left edge
      // ("...at?!"); on the right rail (portrait, readout top-right) grow LEFT hugging the anchor as
      // before. Then stage-clamp the whole card inside the viewport (≥8px) as defence-in-depth. Routed
      // through the shared caption slot, so this one fix covers every BYT3 bark AND the result-surface bark.
      const stageW = sc.scale.width;
      const land = stageW > sc.scale.height;
      const growRight = x < stageW / 2;
      const anchorOrigin = growRight ? 0 : 1; // 0 → left edge at local x=0 (grow right); 1 → right edge (grow left)
      const plate = sc.add
        .text(0, 0, `${RIVAL_NAME}: “${line}”`, {
          fontFamily: FONT.display,
          fontSize: land ? '14px' : '16px',
          fontStyle: 'bold italic',
          color: '#ffc9f2',
          backgroundColor: 'rgba(16,7,20,0.88)',
          padding: { x: 7, y: 4 },
          stroke: '#1a0b18',
          strokeThickness: 2,
        })
        .setOrigin(anchorOrigin, 0);
      const parts: Phaser.GameObjects.GameObject[] = [plate];
      let cardH = plate.height;
      // Horizontal extents of the card in LOCAL coords (relative to the container origin at the anchor).
      let leftExtent = growRight ? 0 : -plate.width;
      let rightExtent = growRight ? plate.width : 0;
      if (emote !== undefined && emotePortraitsReady()) {
        const disp = land ? 58 : 76; // landscape left rail has less vertical clearance above SCORE.
        const portrait = sc.add.image(0, 0, tex(emoteTexKey(emote))).setOrigin(anchorOrigin, 0); // top-anchored
        const src = Math.max(portrait.width, portrait.height, 1);
        const baseScale = disp / src;
        const dispW = portrait.width * baseScale;
        const dispH = portrait.height * baseScale;
        cardH = dispH;
        portrait.setScale(baseScale);
        // Reading order stays [portrait][text] both ways; center the (short) text against the (tall) portrait.
        const plateY = Math.max(0, (dispH - plate.height) / 2);
        if (growRight) {
          portrait.setPosition(0, 0);
          plate.setPosition(dispW + 10, plateY);
          leftExtent = 0;
          rightExtent = dispW + 10 + plate.width;
        } else {
          portrait.setPosition(-plate.width - 10, 0);
          plate.setPosition(0, plateY);
          leftExtent = -plate.width - 10 - dispW;
          rightExtent = 0;
        }
        parts.unshift(portrait);
        if (motionAllowed()) {
          // Pop-in overshoot + a subtle talking bob so BYT3 reads as ALIVE while the line is up.
          portrait.setScale(baseScale * 0.55);
          sc.tweens.add({ targets: portrait, scaleX: baseScale, scaleY: baseScale, duration: 300, ease: 'Back.easeOut' });
          sc.tweens.add({
            targets: portrait,
            y: '-=3',
            duration: 230,
            ease: 'Sine.easeInOut',
            yoyo: true,
            repeat: 5, // covers the ~1.7s hold; killed early when the card destroys
            delay: 300,
          });
        }
      }
      // Clamp the card so its bottom (card body + the 14px float drift) never crosses the grid top —
      // the emote portrait always floats ABOVE the board, never occluding a live tile label.
      let cy = Math.min(y, gridCeiling(sc) - cardH - 16);
      // Stage-clamp horizontally (≥8px each side) whichever way the card grew.
      const margin = 8;
      let cardX = x;
      if (cardX + leftExtent < margin) cardX += margin - (cardX + leftExtent);
      else if (cardX + rightExtent > stageW - margin) cardX -= cardX + rightExtent - (stageW - margin);
      // R1 CARRY-IN (S6): the board-start bark plate marginally overlapped the centred 'N TO WIN'
      // win-status readout at 100% text scale. When the card's horizontal span crosses the live
      // readout AND its top intrudes into that row, drop the card just below the readout line (still
      // re-clamped above the grid) so the plate clears it. No-op on surfaces without a win-status
      // readout (Result) or when there's no horizontal collision (150% scale, where it was already clean).
      const ws = (sc as Partial<{ winStatusText?: Phaser.GameObjects.Text }>).winStatusText;
      if (ws && ws.active && ws.text) {
        const wb = ws.getBounds();
        const cardLeft = cardX + leftExtent;
        const cardRight = cardX + rightExtent;
        if (cardRight > wb.left - 4 && cardLeft < wb.right + 4 && cy < wb.bottom + 4) {
          cy = Math.min(wb.bottom + 6, gridCeiling(sc) - cardH - 16);
        }
      }
      const score = (sc as Partial<{ scoreText?: Phaser.GameObjects.Text }>).scoreText;
      if (land && growRight && score?.active) {
        const sb = score.getBounds();
        const cardLeft = cardX + leftExtent;
        const cardRight = cardX + rightExtent;
        const overlapsScoreX = cardRight > sb.left - 4 && cardLeft < sb.right + 4;
        const overlapsScoreY = cy + cardH + 14 > sb.top - 4 && cy < sb.bottom + 4;
        if (overlapsScoreX && overlapsScoreY) {
          cy = Math.max(8, sb.top - cardH - 18);
        }
      }
      const card = sc.add.container(cardX, cy, parts).setDepth(9000).setAlpha(0);
      sc.tweens.add({ targets: card, alpha: { from: 0, to: 1 }, duration: 160, yoyo: true, hold: 1700 });
      if (motionAllowed()) sc.tweens.add({ targets: card, y: cy + 14, duration: 2020, ease: 'Sine.easeOut' });
      sc.time.delayedCall(CAPTION_LIFE_MS, () => card.destroy());
      return CAPTION_LIFE_MS;
    },
    CAPTION_PRIORITY.rival,
  );
}

/**
 * Result-surface bark + caption. Board-end lines must fire HERE, not GameScene: its clock dies at
 * the 420ms Result handoff, so barks scheduled there never played (independent review HIGH,
 * 2026-07-02). The caption anchors to the top-right corner (no readout face exists on Result) and
 * shows even under mute — the accessibility contract now covers board AND match ends.
 */
export function resultSceneBark(scene: Phaser.Scene, key: BarkKey, delayMs: number, captionOverride?: string): void {
  scene.time.delayedCall(delayMs, () => {
    const x = scene.scale.width - 12;
    const y = Math.max(64, scene.scale.height * 0.085);
    // S7b: when the match-end grudge line has a recorded VO clip, BYT3 SPEAKS it — the grudge clip
    // REPLACES the generic barkMatchWin|Loss take (both never play). Same yield/throttle gate as the
    // generic path, so timing is unchanged; the emote still keys off `key` (which encodes BYT3's result).
    const grudgeClip = captionOverride ? grudgeLineClipKey(captionOverride) : null;
    if (grudgeClip && audio().hasOptionalBark(grudgeClip)) {
      // VOICE best-effort; CAPTION always (a11y): a throttle/yield-suppressed grudge VO still floats its
      // line — the prior `return` on suppression dropped BOTH and left a board/match end with no persona.
      audio().playOptionalBark(grudgeClip, { minGapMs: 2500 });
      spawnCaption(scene, x, y, captionOverride!, resultEmote(key));
      return;
    }
    // VOICE best-effort; CAPTION always. When suppressed (take < 0) there is no spoken take → caption the
    // grudge override if present, else take 0's line (the a11y line survives voice suppression).
    const take = audio().playBark(key, { minGapMs: 2500 });
    spawnCaption(
      scene,
      x,
      y,
      // captionOverride carries a relationship-aware grudge line (VOICE only); the spoken take is unchanged.
      captionOverride ?? BARK_LINE[key][take >= 0 ? take : 0] ?? BARK_LINE[key][0],
      resultEmote(key),
    );
  });
}
